// WHY — inline SVG (no chart library); null = gap segment; small fixed size
// for table rows + tiles. Renders an array of number | null as a polyline;
// null values break the line into multiple <path> elements (gap segments).
// Empty/insufficient data falls back to a dashed center-line placeholder.

interface Props {
  points: Array<number | null>;
  width?: number;
  height?: number;
  stroke?: string;
  strokeWidth?: number;
  ariaLabel?: string;
}

export function Sparkline({
  points,
  width = 120,
  height = 32,
  stroke = "var(--color-bone-dim)",
  strokeWidth = 1.5,
  ariaLabel,
}: Props) {
  const finite = points.filter((p): p is number => p !== null && Number.isFinite(p));

  if (finite.length < 2) {
    // Dashed center-line placeholder
    const cy = height / 2;
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: "block" }}
        aria-label={ariaLabel}
      >
        <line
          x1={0}
          y1={cy}
          x2={width}
          y2={cy}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray="3 3"
        />
      </svg>
    );
  }

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const range = max - min || 1; // avoid division by zero when all values equal
  const step = width / Math.max(points.length - 1, 1);

  // Map a finite value to a y coordinate with 1px top/bottom padding
  const toY = (p: number) => height - ((p - min) / range) * (height - 2) - 1;

  // Build path data strings, one per continuous segment (split on nulls)
  const segments: string[] = [];
  let current = "";

  points.forEach((p, i) => {
    if (p === null || !Number.isFinite(p)) {
      if (current) {
        segments.push(current);
        current = "";
      }
    } else {
      const x = i * step;
      const y = toY(p);
      if (!current) {
        current = `M ${x} ${y}`;
      } else {
        current += ` L ${x} ${y}`;
      }
    }
  });
  if (current) {
    segments.push(current);
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
      aria-label={ariaLabel}
    >
      {segments.map((d, idx) => (
        <path
          key={idx}
          d={d}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
